/**
 * SectorReadinessButton.tsx
 *
 * On-demand button for checking Sector-level readiness.
 * Evaluates if the sector template has sufficient research for AEO content generation.
 * Does NOT check product catalog enrichment (that's site-specific).
 *
 * Checks:
 * - Personas defined and complete
 * - Benefits mapped to categories
 * - Category research done
 * - Sector question wheels created
 * - Attribute mappings defined
 *
 * Created: 2025-12-02
 */

import React, { useState } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Target,
  HelpCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface Props {
  sectorId: number;
  categoryPath: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

interface SectorReadinessData {
  score: number;
  status: 'ready' | 'mostly_ready' | 'needs_work' | 'not_ready';
  categorySpecific: boolean;
  dimensions: {
    personas: { score: number; count: number; complete: number };
    categoryDefinition: { score: number; hasDefinition: boolean | null; researched: number; total: number };
    benefits: { score: number; count: number };
    personaMappings: { score: number; count: number };
    questionWheels: { score: number; count: number; questions: number };
    attributeMappings: { score: number; count: number };
  };
  recommendations: string[];
}

const statusConfig = {
  ready: {
    color: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300',
    icon: CheckCircle2,
    label: 'Sector Ready'
  },
  mostly_ready: {
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300',
    icon: AlertCircle,
    label: 'Mostly Ready'
  },
  needs_work: {
    color: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300',
    icon: AlertTriangle,
    label: 'Needs Work'
  },
  not_ready: {
    color: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
    icon: XCircle,
    label: 'Not Ready'
  }
};

export const SectorReadinessButton: React.FC<Props> = ({
  sectorId,
  categoryPath,
  variant = 'outline',
  size = 'sm',
  className = ''
}) => {
  const [state, setState] = useState<'idle' | 'loading' | 'loaded'>('idle');
  const [data, setData] = useState<SectorReadinessData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkReadiness = async () => {
    if (state === 'loaded' && data) {
      // Second click - open the dialog
      setDialogOpen(true);
      return;
    }

    // First click - load the data
    setState('loading');
    setError(null);

    try {
      const encodedCategory = encodeURIComponent(categoryPath);
      const response = await authFetch(
        `/api/sector-management/sectors/${sectorId}/readiness?categoryPath=${encodedCategory}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
          setState('loaded');
        } else {
          setError('Failed to analyze');
          setState('idle');
        }
      } else {
        setError('Failed to analyze');
        setState('idle');
      }
    } catch (err) {
      console.error('Failed to load sector readiness:', err);
      setError('Analysis failed');
      setState('idle');
    }
  };

  // Idle state - show the question button
  if (state === 'idle') {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={checkReadiness}
        className={`${className}`}
      >
        <Target className="h-4 w-4 mr-1.5" />
        Sector Ready?
      </Button>
    );
  }

  // Loading state
  if (state === 'loading') {
    return (
      <Button
        variant={variant}
        size={size}
        disabled
        className={`${className}`}
      >
        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        Analyzing...
      </Button>
    );
  }

  // Loaded state - show the score badge that can be clicked for details
  if (state === 'loaded' && data) {
    const config = statusConfig[data.status];
    const Icon = config.icon;

    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={`${config.color} cursor-pointer hover:opacity-80 transition-opacity px-2.5 py-1 text-sm ${className}`}
                onClick={() => setDialogOpen(true)}
              >
                <Icon className="h-4 w-4 mr-1.5" />
                {data.score}%
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <div className="space-y-1.5 text-xs">
                <div className="font-semibold">{config.label} ({data.score}/100)</div>
                {data.categorySpecific ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      {data.dimensions.categoryDefinition.score >= 70 ? (
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span>Definition: {data.dimensions.categoryDefinition.hasDefinition ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {data.dimensions.benefits.score >= 70 ? (
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span>Benefits: {data.dimensions.benefits.count} defined</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {data.dimensions.personaMappings.score >= 70 ? (
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span>Personas: {data.dimensions.personaMappings.count} mapped</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5">
                      {data.dimensions.personas.score >= 70 ? (
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span>Personas: {data.dimensions.personas.complete}/{data.dimensions.personas.count}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {data.dimensions.categoryDefinition.score >= 70 ? (
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span>Research: {data.dimensions.categoryDefinition.researched} categories</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {data.dimensions.questionWheels.score >= 70 ? (
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                      ) : (
                        <XCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span>Questions: {data.dimensions.questionWheels.questions} total</span>
                    </div>
                  </>
                )}
                <div className="text-muted-foreground pt-1 border-t mt-1">
                  Click for full analysis
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Sector Readiness Analysis
              </DialogTitle>
            </DialogHeader>
            <SectorReadinessPanel data={data} />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return null;
};

// Internal panel component for the dialog
const SectorReadinessPanel: React.FC<{ data: SectorReadinessData }> = ({ data }) => {
  const config = statusConfig[data.status];
  const StatusIcon = config.icon;

  // Build dimensions based on whether this is category-specific or sector-wide
  const dimensions = data.categorySpecific ? [
    // Category-specific dimensions (higher weight)
    {
      key: 'categoryDefinition',
      label: 'Category Definition',
      score: data.dimensions.categoryDefinition.score,
      detail: data.dimensions.categoryDefinition.hasDefinition ? 'Has definition' : 'No definition',
      tooltip: 'This specific category needs a description/definition',
      weight: '30%'
    },
    {
      key: 'benefits',
      label: 'Category Benefits',
      score: data.dimensions.benefits.score,
      detail: `${data.dimensions.benefits.count} benefits`,
      tooltip: 'Benefits defined for this specific category',
      weight: '25%'
    },
    {
      key: 'personaMappings',
      label: 'Persona Mappings',
      score: data.dimensions.personaMappings.score,
      detail: `${data.dimensions.personaMappings.count} personas mapped`,
      tooltip: 'Personas with priority rankings for this category',
      weight: '20%'
    },
    // Sector foundation dimensions (lower weight)
    {
      key: 'personas',
      label: 'Sector Personas',
      score: data.dimensions.personas.score,
      detail: `${data.dimensions.personas.complete}/${data.dimensions.personas.count} complete`,
      tooltip: 'Target customer personas at sector level (foundation)',
      weight: '10%'
    },
    {
      key: 'questionWheels',
      label: 'Question Wheels',
      score: data.dimensions.questionWheels.score,
      detail: `${data.dimensions.questionWheels.count} wheels, ${data.dimensions.questionWheels.questions} questions`,
      tooltip: 'Question wheels at sector level (foundation)',
      weight: '10%'
    },
    {
      key: 'attributeMappings',
      label: 'Attribute Mappings',
      score: data.dimensions.attributeMappings.score,
      detail: `${data.dimensions.attributeMappings.count} mappings`,
      tooltip: 'Technical attributes mapped to benefits (foundation)',
      weight: '5%'
    }
  ] : [
    // Sector-wide dimensions
    {
      key: 'personas',
      label: 'Personas',
      score: data.dimensions.personas.score,
      detail: `${data.dimensions.personas.complete}/${data.dimensions.personas.count} complete`,
      tooltip: 'Target customer personas with demographics, needs, and pain points',
      weight: '20%'
    },
    {
      key: 'categoryDefinition',
      label: 'Category Research',
      score: data.dimensions.categoryDefinition.score,
      detail: `${data.dimensions.categoryDefinition.researched}/${data.dimensions.categoryDefinition.total} researched`,
      tooltip: 'Categories with research/definitions across the sector',
      weight: '25%'
    },
    {
      key: 'benefits',
      label: 'Benefit Mappings',
      score: data.dimensions.benefits.score,
      detail: `${data.dimensions.benefits.count} mappings`,
      tooltip: 'Benefits mapped to categories and personas',
      weight: '20%'
    },
    {
      key: 'questionWheels',
      label: 'Question Wheels',
      score: data.dimensions.questionWheels.score,
      detail: `${data.dimensions.questionWheels.count} wheels, ${data.dimensions.questionWheels.questions} questions`,
      tooltip: 'Question wheels with customer queries',
      weight: '25%'
    },
    {
      key: 'attributeMappings',
      label: 'Attribute Mappings',
      score: data.dimensions.attributeMappings.score,
      detail: `${data.dimensions.attributeMappings.count} mappings`,
      tooltip: 'Technical attributes mapped to customer-perceived benefits',
      weight: '10%'
    }
  ];

  return (
    <div className="space-y-4">
      {/* Overall Score */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl font-bold">{data.score}/100</span>
            <Badge className={`${config.color}`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {config.label}
            </Badge>
          </div>
          <Progress value={data.score} className="h-3" />
        </div>
      </div>

      {/* Note about what's being checked */}
      <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
        {data.categorySpecific
          ? 'Checking if THIS SPECIFIC CATEGORY has research ready. Product enrichment is evaluated per-site.'
          : 'Checking sector template completeness overall. Product enrichment is evaluated per-site.'}
      </p>

      {/* Dimension Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {data.categorySpecific ? 'Category & Sector Readiness' : 'Dimension Scores'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <TooltipProvider>
            {dimensions.map((dim) => (
              <Tooltip key={dim.key}>
                <TooltipTrigger asChild>
                  <div className="space-y-1 cursor-help">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5">
                        {dim.label}
                        <span className="text-[10px] text-muted-foreground">({dim.weight})</span>
                      </span>
                      <span className={dim.score < 50 ? 'text-orange-600 font-medium' : ''}>
                        {dim.score}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={dim.score} className="h-1.5 flex-1" />
                      <span className="text-xs text-muted-foreground w-36 text-right">
                        {dim.detail}
                      </span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  <p className="text-sm">{dim.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Recommendations */}
      {data.recommendations.length > 0 && (
        <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-orange-800 dark:text-orange-300">
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1 list-disc list-inside text-orange-700 dark:text-orange-400">
              {data.recommendations.map((rec, i) => (
                <li key={i}>{rec}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SectorReadinessButton;
